import React, { useEffect, useRef } from 'react';
import Footer from './Footer';
import { signInWithGoogle } from '../services/auth';

interface ContactPageProps {
  initialSubject?: string;
  autoScroll?: boolean;
}

const ContactPage: React.FC<ContactPageProps> = ({ initialSubject = '', autoScroll = false }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const heroImageRef = useRef<HTMLImageElement>(null);

  // Handle auto-scroll to top when requested
  useEffect(() => {
    if (autoScroll && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [autoScroll]);

  // Parallax Effect
  useEffect(() => {
      const container = scrollContainerRef.current;
      const image = heroImageRef.current;
      
      if (!container || !image) return;

      let rAFId: number;

      const handleScroll = () => {
          const scrollTop = container.scrollTop;
          cancelAnimationFrame(rAFId);
          rAFId = requestAnimationFrame(() => {
              if (image) {
                  // Parallax speed 0.5
                  image.style.transform = `translate3d(0, ${scrollTop * 0.5}px, 0)`;
              }
          });
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
          container.removeEventListener('scroll', handleScroll);
          cancelAnimationFrame(rAFId);
      };
  }, []);

  const handleEmailClick = (e: React.MouseEvent) => {
      e.preventDefault();
      const p1 = 'info';
      const p2 = 'edgarcayce.ch';
      window.location.href = `mailto:${p1}@${p2}?subject=${encodeURIComponent(initialSubject)}`;
  };

  const handleSignIn = async () => {
      try {
          await signInWithGoogle();
      } catch (e) {
          console.error("Sign in failed", (e as any).message || e);
      }
  };

  return (
    <div ref={scrollContainerRef} className="w-full h-full overflow-y-auto bg-[#fbfaf6] dark:bg-stone-900 relative custom-scrollbar">
        
        {/* Parallax Hero Section */}
        <div className="relative h-[70vh] min-h-[500px] w-full overflow-hidden bg-stone-900">
            <img 
                ref={heroImageRef}
                src="https://images.unsplash.com/photo-1523821741446-edb2b68bb7a0?q=80&w=2000&auto=format&fit=crop" 
                alt="Community Journey" 
                className="w-full h-[120%] object-cover object-center absolute -top-[10%] left-0 opacity-80 will-change-transform"
                style={{ willChange: 'transform' }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-stone-900/90"></div>
            
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 pt-20 z-10">
                <p className="text-amber-400 font-bold tracking-[0.2em] uppercase text-sm md:text-base mb-4 animate-in fade-in slide-in-from-bottom-4 duration-1000 ease-out">
                    seeking more?
                </p>
                <h1 className="text-5xl md:text-7xl font-serif text-white mb-8 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200 ease-out drop-shadow-lg">
                    Let's grow community!
                </h1>
                <button 
                    onClick={handleSignIn}
                    className="group flex flex-col items-center gap-2 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-500"
                >
                    <span className="text-white/90 text-lg md:text-xl font-light group-hover:text-white group-hover:scale-105 transition-all duration-300">
                        Sign in to join the discussion
                    </span>
                    <div className="h-px w-16 bg-amber-500/80 group-hover:w-full group-hover:bg-amber-400 transition-all duration-500"></div>
                </button>
            </div>
        </div>

        {/* Content Body (Overlapping) */}
        <div className="relative z-20 -mt-24 px-6 pb-24">
            <div className="max-w-3xl mx-auto bg-white dark:bg-stone-800 rounded-2xl shadow-2xl border border-stone-200 dark:border-stone-700 p-8 md:p-12 text-center animate-in fade-in zoom-in-95 duration-700 delay-300">
                <div className="w-16 h-16 bg-[#ed9f3d]/10 text-[#ed9f3d] rounded-full flex items-center justify-center mx-auto mb-6 text-2xl shadow-inner">
                    <i className="fa-solid fa-paper-plane"></i>
                </div>
                
                <h3 className="text-2xl font-bold text-stone-800 dark:text-white mb-4 font-serif">Get in Touch</h3>
                <p className="text-stone-600 dark:text-stone-300 mb-8 leading-relaxed">
                    Have a question about the archives, a research proposal, or technical feedback? 
                    Our team is dedicated to preserving and expanding access to this information.
                </p>

                <button 
                    onClick={handleEmailClick}
                    className="inline-flex items-center gap-3 bg-[#1e2a4a] text-white px-8 py-4 rounded-full font-bold uppercase tracking-wide hover:bg-[#ed9f3d] hover:text-[#1e2a4a] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                    <span>Send Email</span>
                    <i className="fa-solid fa-arrow-right"></i>
                </button>

                <div className="mt-8 pt-8 border-t border-stone-100 dark:border-stone-700">
                    <p className="text-xs text-stone-400 font-bold uppercase tracking-widest mb-2">Direct Address</p>
                    <p className="font-serif italic text-stone-500 dark:text-stone-400">
                        Please use the button above to contact administration.
                    </p>
                </div>
            </div>
        </div>
        
        <Footer />
    </div>
  );
};

export default ContactPage;